import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Assignment,
  AssignmentTurnedIn,
  EventBusy,
  FlightTakeoff,
  Gavel,
  HowToReg,
  LocalAirport,
  Refresh,
  ArrowForward,
  MoreVert,
  Assessment,
  Timeline,
  Videocam,
  Drafts,
  FactCheck,
  PersonOff,
  Flight,
  Person,
  Groups,
  AirplanemodeActive
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { formatDuration } from './CrewDutyRoster/utils/crewDutyRosterUtils';

const THEME = {
  ink: '#0b1f33',
  navy: '#132a43',
  steel: '#3d5a80',
  mist: '#e8eef5',
  paper: '#f4f7fb',
  card: '#ffffff',
  border: '#d5dee9',
  gold: '#9a7b4f',
  goldSoft: '#c4a574',
  success: '#1b6b4a',
  warning: '#a16207',
  info: '#1d4f7c',
  muted: '#5c6b7a',
  ops: '#c05621',
  flight: '#2e7d32'
};

const EMPTY_COUNTS = {
  drafts: 0,
  pendingOps: 0,
  crewAssignment: 0,
  awaitingAmApproval: 0,
  awaitingCamoApproval: 0,
  crewRejected: 0,
  baQueue: 0,
  atcQueue: 0,
  leavePending: 0
};

const formatShortTime = (date) => {
  if (!date) return null;
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(date));
  } catch {
    return null;
  }
};

const isDutyActiveNow = (trip, nowMs) => {
  if (!trip) return false;
  if (trip.status === 'IN_PROGRESS') return true;
  const start = trip.dutyStartTime ? new Date(trip.dutyStartTime).getTime() : null;
  const end = trip.dutyEndTime ? new Date(trip.dutyEndTime).getTime() : null;
  if (start == null || end == null || Number.isNaN(start) || Number.isNaN(end)) return false;
  return start <= nowMs && end > nowMs;
};

/** Group ON_DUTY crew (crew hours) into live flight cards with trip / aircraft enrichment. */
const buildLiveFlights = ({ crewRestHours = [], pilotHoursByAircraft = [] } = {}) => {
  const nowMs = Date.now();
  const enrichmentByCharter = new Map();

  (pilotHoursByAircraft || []).forEach((row) => {
    (row.trips || []).forEach((trip) => {
      if (trip?.dutyType && trip.dutyType !== 'FLIGHT' && trip.dutyType !== 'TRAINING') return;
      if (!isDutyActiveNow(trip, nowMs)) return;
      const key = trip.charterNumber || String(trip.entryId || '');
      if (!key) return;
      if (!enrichmentByCharter.has(key)) {
        enrichmentByCharter.set(key, {
          charterNumber: trip.charterNumber || null,
          tripTitle: trip.tripTitle || null,
          flightType: trip.flightType || null,
          route: trip.route || null,
          departureAirport: trip.departureAirport || null,
          arrivalAirport: trip.arrivalAirport || null,
          sectorSeqNo: trip.sectorSeqNo ?? null,
          takeOffTime: trip.takeOffTime || null,
          landingTime: trip.landingTime || null,
          dutyStartTime: trip.dutyStartTime || null,
          dutyEndTime: trip.dutyEndTime || null,
          aircraftRegistration: row.aircraftRegistration || null,
          aircraftType: row.aircraftType || null
        });
      }
    });
  });

  const flightsByKey = new Map();
  const onDutyCrew = (crewRestHours || []).filter((row) => {
    if (row?.status !== 'ON_DUTY') return false;
    const dutyType = String(row.currentDutyType || '').toUpperCase();
    // Live flight panel: flight / flight-training duty, or unknown type still on duty
    return !dutyType || dutyType === 'FLIGHT' || dutyType.includes('FLIGHT');
  });

  onDutyCrew.forEach((crew) => {
    const charterKey = crew.currentCharterNumber || null;
    const key = charterKey || `crew-${crew.crewId}`;
    const enrich = charterKey ? enrichmentByCharter.get(charterKey) : null;
    const existing = flightsByKey.get(key) || {
      key,
      charterNumber: charterKey || enrich?.charterNumber || null,
      tripTitle: enrich?.tripTitle || null,
      flightType: enrich?.flightType || null,
      route:
        enrich?.route ||
        (enrich?.departureAirport && enrich?.arrivalAirport
          ? `${enrich.departureAirport} → ${enrich.arrivalAirport}`
          : null),
      sectorSeqNo: enrich?.sectorSeqNo ?? null,
      aircraftRegistration: enrich?.aircraftRegistration || null,
      aircraftType: enrich?.aircraftType || null,
      takeOffTime: enrich?.takeOffTime || null,
      landingTime: enrich?.landingTime || null,
      dutyStartTime: enrich?.dutyStartTime || crew.currentDutyStart || null,
      dutyEndTime: enrich?.dutyEndTime || crew.currentDutyEnd || null,
      dutyType: crew.currentDutyType || enrich?.flightType || null,
      pilots: []
    };

    existing.pilots.push({
      crewId: crew.crewId,
      crewName: crew.crewName || '—',
      hoursOnDuty: crew.hoursOnDuty,
      dutyType: crew.currentDutyType || null,
      dutyStart: crew.currentDutyStart || null,
      dutyEnd: crew.currentDutyEnd || null
    });

    if (!existing.dutyStartTime && crew.currentDutyStart) existing.dutyStartTime = crew.currentDutyStart;
    if (!existing.dutyEndTime && crew.currentDutyEnd) existing.dutyEndTime = crew.currentDutyEnd;
    if (!existing.dutyType && crew.currentDutyType) existing.dutyType = crew.currentDutyType;

    flightsByKey.set(key, existing);
  });

  // Active FLIGHT duties with charter but no ON_DUTY rest row yet — still show the leg
  enrichmentByCharter.forEach((enrich, charterKey) => {
    if (flightsByKey.has(charterKey)) return;
    flightsByKey.set(charterKey, {
      key: charterKey,
      charterNumber: enrich.charterNumber,
      tripTitle: enrich.tripTitle,
      flightType: enrich.flightType,
      route: enrich.route,
      sectorSeqNo: enrich.sectorSeqNo,
      aircraftRegistration: enrich.aircraftRegistration,
      aircraftType: enrich.aircraftType,
      takeOffTime: enrich.takeOffTime,
      landingTime: enrich.landingTime,
      dutyStartTime: enrich.dutyStartTime,
      dutyEndTime: enrich.dutyEndTime,
      dutyType: 'FLIGHT',
      pilots: []
    });
  });

  return Array.from(flightsByKey.values()).sort((a, b) => {
    const aStart = a.dutyStartTime ? new Date(a.dutyStartTime).getTime() : 0;
    const bStart = b.dutyStartTime ? new Date(b.dutyStartTime).getTime() : 0;
    return bStart - aStart;
  });
};

const localDateKey = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const BLOCK_REASON_LABELS = {
  DUTY: 'On duty / assignment',
  SCHEDULED_DUTY_OFF: 'Scheduled off',
  ON_LEAVE: 'On leave',
  UNAVAILABLE: 'Unavailable',
  TRAINING: 'Training',
  MEDICAL: 'Medical',
  OTHER: 'Unavailable'
};

/** Roster/assignment availability for "now" from calendar/availability day row. */
const evaluatePilotAvailabilityNow = (dayAvailability, now = new Date()) => {
  if (!dayAvailability) {
    return { available: true, note: null, reason: null };
  }
  if (dayAvailability.isFullyUnavailable) {
    const block = (dayAvailability.blockedPeriods || [])[0];
    const reason = block?.type || 'UNAVAILABLE';
    return {
      available: false,
      reason,
      note: BLOCK_REASON_LABELS[reason] || reason
    };
  }

  const nowMs = now.getTime();
  const activeBlock = (dayAvailability.blockedPeriods || []).find((bp) => {
    const start = new Date(bp.start).getTime();
    const end = new Date(bp.end).getTime();
    return !Number.isNaN(start) && !Number.isNaN(end) && start <= nowMs && end > nowMs;
  });

  if (activeBlock) {
    const reason = activeBlock.type || 'UNAVAILABLE';
    return {
      available: false,
      reason,
      note: BLOCK_REASON_LABELS[reason] || reason
    };
  }

  if (dayAvailability.isRosterPlanWorkOnlyDay || dayAvailability.hasRosterPlanWork) {
    return { available: true, note: 'Rostered to work', reason: null };
  }
  if (dayAvailability.isFullyAvailable) {
    return { available: true, note: 'Free today', reason: null };
  }
  return { available: true, note: 'Free now', reason: null };
};

/** Only imminent/live ops legs — future ASSIGNED sectors must not hide the aircraft “now”. */
const AIRCRAFT_BUSY_SECTOR_STATUSES = new Set([
  'READY_FOR_BA',
  'BA_PASSED',
  'READY_FOR_ATC',
  'ATC_PASSED',
  'CLEARED'
]);

/**
 * Aircraft maintenance unavailability is planning-window only.
 * WO status (including IN_PROGRESS) never blocks a flight by itself — only the
 * Planning schedule window (FULL_DAY day or HOURLY [start, end)) does.
 */
const getPlanningWindowBlock = (wo, now = new Date()) => {
  if (!wo || ['CANCELLED', 'COMPLETED'].includes(String(wo.status || ''))) return null;
  if (!wo.scheduledDate) return null;

  const start = new Date(wo.scheduledDate);
  if (Number.isNaN(start.getTime())) return null;

  const mode = String(wo.scheduleMode || 'FULL_DAY').toUpperCase();
  let end = null;
  let overlaps = false;

  if (mode === 'HOURLY' && wo.scheduledEndDate) {
    end = new Date(wo.scheduledEndDate);
    if (Number.isNaN(end.getTime()) || !(end.getTime() > start.getTime())) return null;
    overlaps = start.getTime() <= now.getTime() && end.getTime() > now.getTime();
  } else {
    // FULL_DAY: whole calendar day of the planning scheduledDate
    overlaps = localDateKey(start) === localDateKey(now);
    if (overlaps) {
      const dayStart = new Date(now);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(now);
      dayEnd.setHours(23, 59, 59, 999);
      end = dayEnd;
    }
  }

  if (!overlaps) return null;

  const woLabel = wo.workOrderNumber || wo.title || 'WO';
  const windowLabel =
    mode === 'HOURLY' && end
      ? `Hourly ${formatShortTime(start)}–${formatShortTime(end)}`
      : `Full day ${localDateKey(start)}`;

  return {
    category: 'planning_window',
    note: `Planning window · ${woLabel}`,
    windowLabel,
    workOrderNumber: wo.workOrderNumber || null,
    status: wo.status || null
  };
};

const LiveFlightsPanel = ({ flights, loading, onOpenCrewDuty }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        p: 2.5,
        borderRadius: 2.5,
        border: `1px solid ${THEME.border}`,
        bgcolor: THEME.card,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 320
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Flight sx={{ color: THEME.ops, fontSize: 20 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink }}>
            Live flights
          </Typography>
        </Stack>
        {!loading && (
          <Chip
            size="small"
            label={`${flights.length} live`}
            sx={{
              height: 22,
              fontWeight: 700,
              bgcolor: flights.length ? alpha(THEME.ops, 0.12) : THEME.mist,
              color: flights.length ? THEME.ops : THEME.muted,
              border: `1px solid ${flights.length ? alpha(THEME.ops, 0.25) : THEME.border}`
            }}
          />
        )}
      </Stack>
      <Typography variant="body2" sx={{ color: THEME.muted, mb: 1.5 }}>
        Crew currently on duty (crew hours) with trip and pilot detail.
      </Typography>

      {loading ? (
        <Box sx={{ flexGrow: 1, display: 'grid', placeItems: 'center', minHeight: 220 }}>
          <CircularProgress size={32} sx={{ color: THEME.steel }} />
        </Box>
      ) : flights.length === 0 ? (
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 220,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 2,
            bgcolor: alpha(THEME.success, 0.06),
            border: `1px dashed ${alpha(THEME.success, 0.35)}`
          }}
        >
          <Box sx={{ textAlign: 'center', px: 2 }}>
            <Typography variant="subtitle1" sx={{ color: THEME.success, fontWeight: 700 }}>
              No live flights
            </Typography>
            <Typography variant="body2" sx={{ color: THEME.muted }}>
              No crew are on flight duty right now.
            </Typography>
            <Button
              size="small"
              onClick={onOpenCrewDuty}
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600, color: THEME.navy }}
            >
              Open Crew Duty
            </Button>
          </Box>
        </Box>
      ) : (
        <Stack
          spacing={1.25}
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            maxHeight: 280,
            pr: 0.5,
            mr: -0.5
          }}
        >
          {flights.map((flight) => (
            <Box
              key={flight.key}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(THEME.ops, 0.22)}`,
                background: `linear-gradient(135deg, ${alpha(THEME.ops, 0.06)} 0%, ${THEME.card} 55%)`
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: THEME.ink, lineHeight: 1.3 }}
                    noWrap
                  >
                    {flight.charterNumber || 'On-duty flight'}
                    {flight.sectorSeqNo != null ? ` · Sec ${flight.sectorSeqNo}` : ''}
                  </Typography>
                  {flight.tripTitle && (
                    <Typography variant="caption" sx={{ color: THEME.muted, display: 'block' }} noWrap>
                      {flight.tripTitle}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  icon={<FlightTakeoff sx={{ fontSize: '14px !important' }} />}
                  label={flight.dutyType || 'ON DUTY'}
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    bgcolor: alpha(THEME.ops, 0.14),
                    color: THEME.ops,
                    '& .MuiChip-icon': { color: THEME.ops }
                  }}
                />
              </Stack>

              <Typography variant="body2" sx={{ mt: 0.75, color: THEME.navy, fontWeight: 600 }}>
                {flight.route || 'Route TBD'}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                {flight.aircraftRegistration && (
                  <Chip
                    size="small"
                    label={flight.aircraftRegistration}
                    sx={{ height: 20, fontSize: '0.7rem', bgcolor: THEME.mist, color: THEME.ink }}
                  />
                )}
                {flight.aircraftType && (
                  <Chip
                    size="small"
                    label={flight.aircraftType}
                    sx={{ height: 20, fontSize: '0.7rem', bgcolor: THEME.mist, color: THEME.muted }}
                  />
                )}
                {flight.flightType && (
                  <Chip
                    size="small"
                    label={flight.flightType}
                    sx={{ height: 20, fontSize: '0.7rem', bgcolor: THEME.mist, color: THEME.muted }}
                  />
                )}
              </Stack>

              <Typography variant="caption" sx={{ color: THEME.muted, display: 'block', mt: 0.75 }}>
                {[
                  formatShortTime(flight.takeOffTime || flight.dutyStartTime)
                    ? `Dep ${formatShortTime(flight.takeOffTime || flight.dutyStartTime)}`
                    : null,
                  formatShortTime(flight.landingTime || flight.dutyEndTime)
                    ? `Arr ${formatShortTime(flight.landingTime || flight.dutyEndTime)}`
                    : null
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Duty window from crew hours'}
              </Typography>

              <Divider sx={{ my: 1, borderColor: THEME.border }} />

              {flight.pilots.length === 0 ? (
                <Typography variant="caption" sx={{ color: THEME.muted }}>
                  Pilot detail pending from crew hours.
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {flight.pilots.map((pilot) => (
                    <Stack
                      key={String(pilot.crewId)}
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <Person sx={{ fontSize: 16, color: THEME.steel }} />
                        <Typography variant="body2" sx={{ color: THEME.ink, fontWeight: 600 }} noWrap>
                          {pilot.crewName}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="caption"
                        sx={{ color: THEME.ops, fontWeight: 700, whiteSpace: 'nowrap' }}
                      >
                        {pilot.hoursOnDuty != null ? formatDuration(pilot.hoursOnDuty) : '—'}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

const AvailabilityListCard = ({
  title,
  subtitle,
  count,
  loading,
  emptyLabel,
  accent,
  icon,
  items,
  renderItem,
  onOpen
}) => (
  <Paper
    elevation={0}
    sx={{
      height: '100%',
      p: 2.5,
      borderRadius: 2.5,
      border: `1px solid ${THEME.border}`,
      bgcolor: THEME.card,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 280
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: accent, display: 'grid', placeItems: 'center' }}>{icon}</Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink }}>
          {title}
        </Typography>
      </Stack>
      {!loading && (
        <Chip
          size="small"
          label={`${count} available`}
          sx={{
            height: 22,
            fontWeight: 700,
            bgcolor: count ? alpha(accent, 0.12) : THEME.mist,
            color: count ? accent : THEME.muted,
            border: `1px solid ${count ? alpha(accent, 0.25) : THEME.border}`
          }}
        />
      )}
    </Stack>
    <Typography variant="body2" sx={{ color: THEME.muted, mb: 1.5 }}>
      {subtitle}
    </Typography>

    {loading ? (
      <Box sx={{ flexGrow: 1, display: 'grid', placeItems: 'center', minHeight: 180 }}>
        <CircularProgress size={28} sx={{ color: THEME.steel }} />
      </Box>
    ) : items.length === 0 ? (
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 180,
          display: 'grid',
          placeItems: 'center',
          borderRadius: 2,
          bgcolor: THEME.mist,
          border: `1px dashed ${THEME.border}`
        }}
      >
        <Typography variant="body2" sx={{ color: THEME.muted }}>
          {emptyLabel}
        </Typography>
      </Box>
    ) : (
      <Stack
        spacing={0.75}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          maxHeight: 260,
          pr: 0.5,
          mr: -0.5
        }}
      >
        {items.map((item) => renderItem(item))}
      </Stack>
    )}

    <Button
      size="small"
      onClick={onOpen}
      sx={{ mt: 1.5, alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, color: THEME.navy }}
    >
      Open full list
    </Button>
  </Paper>
);

const formatUpdatedAt = (date) => {
  if (!date) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(date);
};

const ChartTooltipContent = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1,
        border: `1px solid ${THEME.border}`,
        bgcolor: THEME.card,
        borderRadius: 1.5,
        boxShadow: `0 8px 24px ${alpha(THEME.ink, 0.12)}`
      }}
    >
      <Typography variant="caption" sx={{ color: THEME.muted, display: 'block' }}>
        {item.name || item.payload?.name}
      </Typography>
      <Typography variant="subtitle2" sx={{ color: THEME.ink, fontWeight: 700 }}>
        {item.value}
      </Typography>
    </Paper>
  );
};

const SummaryMetric = ({ label, value, hint, tone = 'navy', loading }) => {
  const toneColor =
    tone === 'gold'
      ? THEME.gold
      : tone === 'success'
        ? THEME.success
        : tone === 'warning'
          ? THEME.warning
          : tone === 'ops'
            ? THEME.ops
            : THEME.navy;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${THEME.border}`,
        background: `linear-gradient(145deg, ${THEME.card} 0%, ${alpha(toneColor, 0.06)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 10px 28px ${alpha(THEME.ink, 0.08)}`
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: toneColor
        }
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: THEME.muted, letterSpacing: 1.2, fontWeight: 600, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography
        variant="h3"
        sx={{
          mt: 0.75,
          mb: 0.5,
          color: THEME.ink,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em'
        }}
      >
        {loading ? '—' : value}
      </Typography>
      <Typography variant="body2" sx={{ color: THEME.muted }}>
        {hint}
      </Typography>
    </Paper>
  );
};

const WorkloadCard = ({ card, loading, onOpen, onOpenTasks }) => {
  const accent = card.accent || THEME.steel;
  const hasItems = !loading && card.value > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2.5,
        border: `1px solid ${hasItems ? alpha(accent, 0.35) : THEME.border}`,
        bgcolor: THEME.card,
        overflow: 'hidden',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          borderColor: alpha(accent, 0.55),
          boxShadow: `0 12px 32px ${alpha(THEME.ink, 0.07)}`,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          background: `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, transparent 100%)`
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(accent, 0.12),
              color: accent,
              flexShrink: 0
            }}
          >
            {card.icon}
          </Box>
          <Chip
            size="small"
            label={card.category}
            sx={{
              height: 22,
              fontSize: '0.68rem',
              fontWeight: 600,
              bgcolor: alpha(accent, 0.1),
              color: accent,
              border: `1px solid ${alpha(accent, 0.2)}`
            }}
          />
        </Stack>
        <Typography
          variant="subtitle1"
          sx={{ mt: 1.5, color: THEME.ink, fontWeight: 700, lineHeight: 1.3 }}
        >
          {card.title}
        </Typography>
      </Box>

      <Box sx={{ px: 2, pb: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="h3"
          sx={{
            color: hasItems ? accent : THEME.muted,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em',
            mb: 0.75
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: THEME.steel }} /> : card.value}
        </Typography>
        <Typography variant="body2" sx={{ color: THEME.muted, flexGrow: 1, mb: 2 }}>
          {card.description}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
            onClick={onOpen}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: THEME.navy,
              boxShadow: 'none',
              '&:hover': { bgcolor: THEME.ink, boxShadow: 'none' }
            }}
          >
            {card.actionLabel}
          </Button>
          <Tooltip title="Focused task shortcuts">
            <IconButton
              size="small"
              onClick={onOpenTasks}
              sx={{
                border: `1px solid ${THEME.border}`,
                borderRadius: 1.25,
                color: THEME.steel
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Paper>
  );
};

const OpsTeamDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSalesRole = user?.role === ROLES.SALES;
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [liveFlights, setLiveFlights] = useState([]);
  const [availablePilots, setAvailablePilots] = useState([]);
  const [availableAircraft, setAvailableAircraft] = useState([]);
  const [taskMenuAnchor, setTaskMenuAnchor] = useState(null);
  const [taskMenuKey, setTaskMenuKey] = useState(null);

  const actionWorkload = useMemo(
    () =>
      (counts.pendingOps || 0) +
      (counts.crewAssignment || 0) +
      (counts.crewRejected || 0) +
      (counts.baQueue || 0) +
      (counts.atcQueue || 0) +
      (counts.leavePending || 0),
    [counts]
  );

  const flightEntryWorkload = useMemo(
    () =>
      (counts.drafts || 0) +
      (counts.awaitingAmApproval || 0) +
      (counts.awaitingCamoApproval || 0),
    [counts]
  );

  const totalOpenItems = actionWorkload + flightEntryWorkload;

  const compositionData = useMemo(
    () =>
      [
        { name: 'Ops action queues', value: actionWorkload, color: THEME.ops },
        { name: 'Flight Entry follow-up', value: flightEntryWorkload, color: THEME.flight }
      ].filter((d) => d.value > 0),
    [actionWorkload, flightEntryWorkload]
  );

  const queueBarData = useMemo(
    () =>
      [
        { name: 'Drafts', value: counts.drafts, fill: THEME.steel },
        { name: 'Pending Ops', value: counts.pendingOps, fill: THEME.ops },
        { name: 'Crew Assign', value: counts.crewAssignment, fill: THEME.warning },
        { name: 'Crew Rejected', value: counts.crewRejected, fill: '#b42318' },
        { name: 'BA Queue', value: counts.baQueue, fill: THEME.info },
        { name: 'ATC Queue', value: counts.atcQueue, fill: '#2a6f97' },
        { name: 'Leave Pending', value: counts.leavePending, fill: '#7a5c45' },
        { name: 'Awaiting AM', value: counts.awaitingAmApproval, fill: THEME.gold },
        { name: 'Awaiting CAMO', value: counts.awaitingCamoApproval, fill: THEME.success }
      ].filter((d) => d.value > 0),
    [counts]
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const todayKey = localDateKey(new Date());
      const [
        tripsResult,
        sectorsResult,
        leaveResult,
        crewDashboardResult,
        pilotsResult,
        aircraftResult,
        calendarResult,
        maintenanceResult
      ] = await Promise.allSettled([
        axios.get('/api/sales/trips'),
        axios.get('/api/ops/sectors', { params: { summary: '1' } }),
        axios.get('/api/ops/crew-duty-roster/leave-requests', { params: { status: 'PENDING' } }),
        axios.get('/api/ops/crew-duty-roster/dashboard'),
        axios.get('/api/ops/crew-duty-roster/assignable-pilots'),
        axios.get('/api/aircraft'),
        axios.get('/api/ops/crew-duty-roster/calendar/availability', {
          params: { startDate: todayKey, endDate: todayKey }
        }),
        axios.get('/api/maintenance')
      ]);

      const trips = tripsResult.status === 'fulfilled' ? tripsResult.value.data || [] : [];
      const sectors = sectorsResult.status === 'fulfilled' ? sectorsResult.value.data || [] : [];
      const leaveRequests = leaveResult.status === 'fulfilled' ? leaveResult.value.data || [] : [];
      const crewDashboard =
        crewDashboardResult.status === 'fulfilled' ? crewDashboardResult.value.data || {} : {};
      const pilots = pilotsResult.status === 'fulfilled' ? pilotsResult.value.data || [] : [];
      const aircraft = aircraftResult.status === 'fulfilled' ? aircraftResult.value.data || [] : [];
      const calendarAvailability =
        calendarResult.status === 'fulfilled' ? calendarResult.value.data || [] : [];
      const maintenanceOrders =
        maintenanceResult.status === 'fulfilled' ? maintenanceResult.value.data || [] : [];

      const drafts = trips.filter((trip) => trip?.status === 'DRAFT').length;
      const pendingOps = trips.filter((trip) => trip?.status === 'PENDING_OPS').length;
      const crewAssignment = trips.filter((trip) => trip?.status === 'CREW_ASSIGNMENT').length;
      const awaitingAmApproval = trips.filter(
        (trip) =>
          trip?.status === 'CREW_ASSIGNMENT' &&
          trip?.approvals?.accountableManager?.approved !== true
      ).length;
      const awaitingCamoApproval = trips.filter(
        (trip) =>
          trip?.status === 'CREW_ASSIGNMENT' &&
          trip?.approvals?.accountableManager?.approved === true &&
          trip?.approvals?.camo?.approved !== true
      ).length;

      const crewRejected = sectors.filter((s) => s?.status === 'CREW_REJECTED').length;
      const baQueue = sectors.filter((s) => s?.status === 'READY_FOR_BA').length;
      const atcQueue = sectors.filter((s) => s?.status === 'READY_FOR_ATC').length;
      const leavePending = Array.isArray(leaveRequests) ? leaveRequests.length : 0;

      setCounts({
        drafts,
        pendingOps,
        crewAssignment,
        awaitingAmApproval,
        awaitingCamoApproval,
        crewRejected,
        baQueue,
        atcQueue,
        leavePending
      });
      const builtLiveFlights = buildLiveFlights({
        crewRestHours: crewDashboard.crewRestHours || [],
        pilotHoursByAircraft: crewDashboard.pilotHoursByAircraft || []
      });
      setLiveFlights(builtLiveFlights);

      const now = new Date();
      const onDutyCrewIds = new Set(
        (crewDashboard.crewRestHours || [])
          .filter((row) => row?.status === 'ON_DUTY')
          .map((row) => String(row.crewId))
      );

      const calendarByCrewId = new Map();
      (Array.isArray(calendarAvailability) ? calendarAvailability : []).forEach((row) => {
        const crewId = String(row?.crew?.id || row?.crew?._id || '');
        if (!crewId) return;
        const todayRow =
          (row.availability || []).find((d) => d.date === todayKey) || (row.availability || [])[0];
        calendarByCrewId.set(crewId, todayRow || null);
      });

      setAvailablePilots(
        (Array.isArray(pilots) ? pilots : [])
          .filter((p) => p?.isPilotActive && p?.isActive !== false)
          .map((p) => {
            const id = String(p._id);
            if (onDutyCrewIds.has(id)) {
              return {
                ...p,
                availabilityNote: 'On duty now',
                isAvailableNow: false
              };
            }
            const day = calendarByCrewId.get(id);
            // External / assignable pilots not on roster calendar: treat as available if not on duty
            if (!calendarByCrewId.has(id)) {
              return {
                ...p,
                availabilityNote: 'Assignable',
                isAvailableNow: true
              };
            }
            const evalResult = evaluatePilotAvailabilityNow(day, now);
            return {
              ...p,
              availabilityNote: evalResult.note || (evalResult.available ? 'Available' : 'Unavailable'),
              isAvailableNow: evalResult.available
            };
          })
          .filter((p) => p.isAvailableNow)
          .sort((a, b) =>
            `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(
              `${b.firstName || ''} ${b.lastName || ''}`
            )
          )
      );

      const planningWindowByAircraftId = new Map();
      (Array.isArray(maintenanceOrders) ? maintenanceOrders : []).forEach((wo) => {
        const block = getPlanningWindowBlock(wo, now);
        if (!block) return;
        const aircraftId = String(wo.aircraftId?._id || wo.aircraftId || '');
        if (!aircraftId || planningWindowByAircraftId.has(aircraftId)) return;
        planningWindowByAircraftId.set(aircraftId, block);
      });

      const sectorBusyAircraftIds = new Set();
      (Array.isArray(sectors) ? sectors : []).forEach((sector) => {
        if (!AIRCRAFT_BUSY_SECTOR_STATUSES.has(String(sector?.status || ''))) return;
        const aircraftId = String(sector.aircraftId?._id || sector.aircraftId || '');
        if (aircraftId) sectorBusyAircraftIds.add(aircraftId);
      });

      const liveAircraftRegs = new Set(
        builtLiveFlights
          .map((f) => String(f.aircraftRegistration || '').trim().toUpperCase())
          .filter(Boolean)
      );

      setAvailableAircraft(
        (Array.isArray(aircraft) ? aircraft : [])
          .filter((a) => a?.isActive !== false)
          .map((a) => {
            const id = String(a._id);
            const status = String(a.status || '').toUpperCase();
            const reg = String(a.registration || '').trim().toUpperCase();
            const planningBlock = planningWindowByAircraftId.get(id);

            // Maintenance unavailability categories:
            // - planning_window: only Planning scheduledDate / HOURLY window (IN_PROGRESS does not block)
            // Other non-maintenance ops categories: grounded, active sector, live flight
            let unavailableCategory = null;
            let availabilityNote = 'Available';

            if (status === 'GROUNDED') {
              unavailableCategory = 'grounded';
              availabilityNote = 'Grounded';
            } else if (planningBlock) {
              unavailableCategory = 'planning_window';
              availabilityNote = planningBlock.note;
            } else if (sectorBusyAircraftIds.has(id)) {
              unavailableCategory = 'active_sector';
              availabilityNote = 'BA/ATC/cleared sector';
            } else if (liveAircraftRegs.has(reg) || status === 'IN_FLIGHT') {
              unavailableCategory = 'live_flight';
              availabilityNote = 'On live flight';
            } else {
              // MAINTENANCE / INSPECTION fleet status alone does not block — planning window only
              availabilityNote =
                status === 'MAINTENANCE' || status === 'INSPECTION'
                  ? 'Available (maint. in progress)'
                  : 'Available';
            }

            return {
              ...a,
              isAvailableNow: !unavailableCategory,
              unavailableCategory,
              availabilityNote
            };
          })
          .filter((a) => a.isAvailableNow)
          .sort((a, b) =>
            String(a.registration || '').localeCompare(String(b.registration || ''))
          )
      );

      setLastUpdated(new Date());
    } catch (error) {
      setCounts(EMPTY_COUNTS);
      setLiveFlights([]);
      setAvailablePilots([]);
      setAvailableAircraft([]);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const taskActionsByCard = useMemo(
    () => ({
      drafts: [
        {
          label: 'Open draft charters in Flight Entry',
          action: () => navigate({ pathname: '/sales', search: '?opsFocus=draft' })
        }
      ],
      pendingOps: [
        {
          label: 'Open charters pending Ops',
          action: () => navigate({ pathname: '/sales', search: '?opsFocus=pending_ops' })
        },
        {
          label: 'Open Operations Board',
          action: () => navigate('/ops')
        }
      ],
      crewAssignment: [
        {
          label: 'Open crew-assignment charters',
          action: () => navigate({ pathname: '/sales', search: '?opsFocus=crew_assignment' })
        },
        {
          label: 'Assign aircraft & crew on Ops Board',
          action: () => navigate('/ops')
        }
      ],
      awaitingAmApproval: [
        {
          label: 'Review charters awaiting AM',
          action: () => navigate({ pathname: '/sales', search: '?amFocus=charter_am' })
        }
      ],
      awaitingCamoApproval: [
        {
          label: 'Review charters awaiting CAMO',
          action: () => navigate({ pathname: '/sales', search: '?amFocus=charter_camo' })
        }
      ],
      crewRejected: [
        {
          label: 'Reassign rejected sectors on Ops Board',
          action: () => navigate('/ops')
        }
      ],
      baQueue: [
        {
          label: 'Open BA queue on Ops Board',
          action: () => navigate('/ops')
        }
      ],
      atcQueue: [
        {
          label: 'Open ATC queue on Ops Board',
          action: () => navigate('/ops')
        }
      ],
      leavePending: [
        {
          label: 'Review pending leave requests',
          action: () => navigate('/crew-duty-roster')
        }
      ]
    }),
    [navigate]
  );

  const navigateCardPrimary = (card) => {
    if (card.navigateState) {
      navigate(card.path, { state: card.navigateState });
      return;
    }
    if (card.search) {
      navigate({ pathname: card.path, search: card.search });
      return;
    }
    navigate(card.path);
  };

  const cards = useMemo(() => {
    const allCards = [
      {
        key: 'drafts',
        title: 'Flight Entry Drafts',
        value: counts.drafts,
        icon: <Drafts fontSize="small" />,
        description: 'Charters still in DRAFT — finish sectors and send to Operations.',
        actionLabel: 'Open Flight Entry',
        path: '/sales',
        search: '?opsFocus=draft',
        category: 'Flight Entry',
        accent: THEME.steel,
        salesPrimary: true
      },
      {
        key: 'pendingOps',
        title: 'Pending Operations',
        value: counts.pendingOps,
        icon: <FlightTakeoff fontSize="small" />,
        description: 'Charters sent to Ops (PENDING_OPS) awaiting aircraft and crew assignment.',
        actionLabel: 'Open Ops Board',
        path: '/ops',
        category: 'Action',
        accent: THEME.ops,
        opsPrimary: true
      },
      {
        key: 'crewAssignment',
        title: 'Crew Assignment Gate',
        value: counts.crewAssignment,
        icon: <HowToReg fontSize="small" />,
        description: 'Trips in CREW_ASSIGNMENT — confirm crew acceptance and AM/CAMO approvals.',
        actionLabel: 'Open Flight Entry',
        path: '/sales',
        search: '?opsFocus=crew_assignment',
        category: 'Action',
        accent: THEME.warning,
        opsPrimary: true
      },
      {
        key: 'crewRejected',
        title: 'Crew Rejected Sectors',
        value: counts.crewRejected,
        icon: <PersonOff fontSize="small" />,
        description: 'Sectors rejected by crew — reassign aircraft or pilots on the Ops Board.',
        actionLabel: 'Open Ops Board',
        path: '/ops',
        category: 'Action',
        accent: '#b42318',
        opsPrimary: true
      },
      {
        key: 'baQueue',
        title: 'BA Test Queue',
        value: counts.baQueue,
        icon: <Videocam fontSize="small" />,
        description: 'Sectors in READY_FOR_BA awaiting breathalyzer compliance upload.',
        actionLabel: 'Open Ops Board',
        path: '/ops',
        category: 'Action',
        accent: THEME.info,
        opsPrimary: true
      },
      {
        key: 'atcQueue',
        title: 'ATC Clearance Queue',
        value: counts.atcQueue,
        icon: <LocalAirport fontSize="small" />,
        description: 'Sectors in READY_FOR_ATC awaiting clearance before flight.',
        actionLabel: 'Open Ops Board',
        path: '/ops',
        category: 'Action',
        accent: '#2a6f97',
        opsPrimary: true
      },
      {
        key: 'leavePending',
        title: 'Leave Requests Pending',
        value: counts.leavePending,
        icon: <EventBusy fontSize="small" />,
        description: 'Crew leave requests waiting Operations approval.',
        actionLabel: 'Open Crew Duty',
        path: '/crew-duty-roster',
        category: 'Crew Duty',
        accent: '#7a5c45',
        opsPrimary: true
      },
      {
        key: 'awaitingAmApproval',
        title: 'Awaiting AM Approval',
        value: counts.awaitingAmApproval,
        icon: <FactCheck fontSize="small" />,
        description: 'Charters with crew assigned still waiting Accountable Manager approval.',
        actionLabel: 'Open Flight Entry',
        path: '/sales',
        search: '?amFocus=charter_am',
        category: 'Follow-up',
        accent: THEME.gold,
        salesPrimary: true
      },
      {
        key: 'awaitingCamoApproval',
        title: 'Awaiting CAMO Approval',
        value: counts.awaitingCamoApproval,
        icon: <AssignmentTurnedIn fontSize="small" />,
        description: 'AM approved; CAMO airworthiness still pending before ops continue.',
        actionLabel: 'Open Flight Entry',
        path: '/sales',
        search: '?amFocus=charter_camo',
        category: 'Follow-up',
        accent: THEME.success,
        salesPrimary: true
      }
    ];

    if (isSalesRole) {
      return allCards
        .filter((card) => card.salesPrimary || ['pendingOps', 'crewAssignment'].includes(card.key))
        .map((card) => {
          if (card.key === 'pendingOps') {
            return {
              ...card,
              actionLabel: 'Open Flight Entry',
              path: '/sales',
              search: '?opsFocus=pending_ops'
            };
          }
          return card;
        });
    }
    return allCards;
  }, [counts, isSalesRole]);

  const shortcuts = useMemo(() => {
    const base = [
      { label: 'Flight Entry', path: '/sales' },
      { label: 'Operations Board', path: '/ops' },
      { label: 'Crew Duty', path: '/crew-duty-roster' },
      { label: 'Technical Log Book', path: '/technical-log-book' }
    ];
    if (isSalesRole) {
      return base.filter((item) => item.path !== '/ops' && item.path !== '/crew-duty-roster');
    }
    return base;
  }, [isSalesRole]);

  const roleChipLabel = isSalesRole ? 'Flight Entry' : 'Head of Operations';

  const heroTitle = isSalesRole ? 'Flight Entry Desk' : 'Head of Operations Dashboard';
  const heroSubtitle = isSalesRole
    ? 'Track draft charters, handoffs to Operations, and approval follow-up across AM and CAMO.'
    : 'Assign aircraft and crew, clear BA/ATC queues, and keep charter handoffs moving from Flight Entry through departure.';

  const chartEmpty = !loading && compositionData.length === 0;

  return (
    <Box
      sx={{
        mx: { xs: -1, sm: -2 },
        mb: -2,
        px: { xs: 1.5, sm: 2.5 },
        pb: 3,
        pt: 0.5,
        minHeight: '100%',
        background: `
          radial-gradient(ellipse 80% 50% at 100% -10%, ${alpha(THEME.ops, 0.12)} 0%, transparent 55%),
          radial-gradient(ellipse 60% 40% at 0% 0%, ${alpha(THEME.flight, 0.1)} 0%, transparent 50%),
          linear-gradient(180deg, ${THEME.paper} 0%, #eef2f7 100%)
        `
      }}
    >
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${THEME.border}`,
          background: `linear-gradient(125deg, ${THEME.ink} 0%, ${THEME.navy} 48%, ${THEME.steel} 100%)`,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `
              linear-gradient(90deg, transparent 60%, ${alpha(THEME.ops, 0.18)} 100%),
              repeating-linear-gradient(
                -12deg,
                transparent,
                transparent 18px,
                ${alpha('#ffffff', 0.015)} 18px,
                ${alpha('#ffffff', 0.015)} 19px
              )
            `,
            pointerEvents: 'none'
          }
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ p: { xs: 2.5, md: 3 }, position: 'relative', zIndex: 1 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.25,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(THEME.ops, 0.25),
                  color: '#ffd7ba',
                  border: `1px solid ${alpha('#ffd7ba', 0.35)}`
                }}
              >
                <Assessment fontSize="small" />
              </Box>
              <Chip
                size="small"
                label={roleChipLabel}
                sx={{
                  bgcolor: alpha('#ffffff', 0.1),
                  color: alpha('#ffffff', 0.9),
                  border: `1px solid ${alpha('#ffffff', 0.18)}`,
                  fontWeight: 600,
                  letterSpacing: 0.4
                }}
              />
            </Stack>
            <Typography
              variant="h4"
              sx={{
                color: '#fff',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                mb: 0.75
              }}
            >
              {heroTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.72), maxWidth: 560 }}>
              {heroSubtitle}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Box sx={{ textAlign: { xs: 'left', md: 'right' }, mr: { md: 1 } }}>
              <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.55), display: 'block' }}>
                Last refreshed
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.9), fontWeight: 600 }}>
                {formatUpdatedAt(lastUpdated)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={fetchData}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: THEME.ops,
                color: '#fff',
                boxShadow: 'none',
                px: 2.25,
                '&:hover': { bgcolor: '#9a3412', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: alpha(THEME.ops, 0.4), color: alpha('#fff', 0.5) }
              }}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <SummaryMetric
            label="Ops action queues"
            value={actionWorkload}
            hint="Pending Ops, crew, BA/ATC, and leave"
            tone="ops"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryMetric
            label="Flight Entry follow-up"
            value={flightEntryWorkload}
            hint="Drafts and AM/CAMO approval wait"
            tone="navy"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryMetric
            label="Total open items"
            value={totalOpenItems}
            hint={totalOpenItems === 0 ? 'All queues clear' : 'Combined ops and flight-entry load'}
            tone={totalOpenItems === 0 ? 'success' : 'warning'}
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              p: 2.5,
              borderRadius: 2.5,
              border: `1px solid ${THEME.border}`,
              bgcolor: THEME.card
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Gavel sx={{ color: THEME.ops, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink }}>
                Workload composition
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: THEME.muted, mb: 2 }}>
              Ops action vs Flight Entry follow-up.
            </Typography>

            {loading ? (
              <Box sx={{ height: 260, display: 'grid', placeItems: 'center' }}>
                <CircularProgress size={32} sx={{ color: THEME.steel }} />
              </Box>
            ) : chartEmpty ? (
              <Box
                sx={{
                  height: 260,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 2,
                  bgcolor: alpha(THEME.success, 0.06),
                  border: `1px dashed ${alpha(THEME.success, 0.35)}`
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: THEME.success, fontWeight: 700 }}>
                    Queues clear
                  </Typography>
                  <Typography variant="body2" sx={{ color: THEME.muted }}>
                    No open Ops or Flight Entry items right now.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ height: 260, position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={compositionData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="46%"
                      innerRadius={52}
                      outerRadius={78}
                      paddingAngle={3}
                      stroke={THEME.card}
                      strokeWidth={3}
                    >
                      {compositionData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<ChartTooltipContent />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => (
                        <span style={{ color: THEME.muted, fontSize: 12 }}>{value}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '42%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  <Typography variant="caption" sx={{ color: THEME.muted, display: 'block' }}>
                    Total
                  </Typography>
                  <Typography variant="h5" sx={{ color: THEME.ink, fontWeight: 700, lineHeight: 1.1 }}>
                    {totalOpenItems}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              p: 2.5,
              borderRadius: 2.5,
              border: `1px solid ${THEME.border}`,
              bgcolor: THEME.card
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Timeline sx={{ color: THEME.steel, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink }}>
                Queue depth by area
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: THEME.muted, mb: 2 }}>
              Active queues only — empty lanes omitted.
            </Typography>

            {loading ? (
              <Box sx={{ height: 260, display: 'grid', placeItems: 'center' }}>
                <CircularProgress size={32} sx={{ color: THEME.steel }} />
              </Box>
            ) : queueBarData.length === 0 ? (
              <Box
                sx={{
                  height: 260,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 2,
                  bgcolor: THEME.mist
                }}
              >
                <Typography variant="body2" sx={{ color: THEME.muted }}>
                  No active queues to chart.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: Math.max(220, queueBarData.length * 36), minHeight: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={queueBarData}
                    layout="vertical"
                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={THEME.border} horizontal={false} />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fill: THEME.muted, fontSize: 11 }}
                      axisLine={{ stroke: THEME.border }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={100}
                      tick={{ fill: THEME.ink, fontSize: 11, fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip content={<ChartTooltipContent />} cursor={{ fill: alpha(THEME.steel, 0.06) }} />
                    <Bar dataKey="value" name="Items" radius={[0, 6, 6, 0]} maxBarSize={22} barSize={18}>
                      {queueBarData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <LiveFlightsPanel
            flights={liveFlights}
            loading={loading}
            onOpenCrewDuty={() => navigate('/crew-duty-roster')}
          />
        </Grid>
      </Grid>

      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ color: THEME.ink, fontWeight: 700 }}>
            Available resources
          </Typography>
          <Typography variant="body2" sx={{ color: THEME.muted }}>
            Pilots free now per roster / leave / duty. Aircraft: only Planning schedule windows
            block for maintenance (in-progress WO status does not).
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <AvailabilityListCard
            title="Available pilots"
            subtitle="Based on today’s duty roster, leave, and availability blocks."
            count={availablePilots.length}
            loading={loading}
            emptyLabel="No pilots free on the roster right now."
            accent={THEME.flight}
            icon={<Groups fontSize="small" />}
            items={availablePilots}
            onOpen={() => navigate('/crew-duty-roster')}
            renderItem={(pilot) => (
              <Stack
                key={String(pilot._id)}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1.25,
                  py: 0.9,
                  borderRadius: 1.5,
                  border: `1px solid ${THEME.border}`,
                  bgcolor: alpha(THEME.flight, 0.03)
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Person sx={{ fontSize: 18, color: THEME.flight }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: THEME.ink }} noWrap>
                      {`${pilot.firstName || ''} ${pilot.lastName || ''}`.trim() || '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: THEME.muted }} noWrap>
                      {[pilot.role, pilot.workLocation].filter(Boolean).join(' · ') || pilot.email || '—'}
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  size="small"
                  label={pilot.availabilityNote || 'Available'}
                  sx={{
                    height: 20,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    bgcolor: alpha(THEME.flight, 0.12),
                    color: THEME.flight
                  }}
                />
              </Stack>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AvailabilityListCard
            title="Available aircraft"
            subtitle="Blocked only by Planning windows (full-day/hourly). In-progress maintenance does not block."
            count={availableAircraft.length}
            loading={loading}
            emptyLabel="No aircraft free of maintenance or assignment right now."
            accent={THEME.info}
            icon={<AirplanemodeActive fontSize="small" />}
            items={availableAircraft}
            onOpen={() => navigate('/ops')}
            renderItem={(ac) => (
              <Stack
                key={String(ac._id)}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1.25,
                  py: 0.9,
                  borderRadius: 1.5,
                  border: `1px solid ${THEME.border}`,
                  bgcolor: alpha(THEME.info, 0.03)
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <LocalAirport sx={{ fontSize: 18, color: THEME.info }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: THEME.ink }} noWrap>
                      {ac.registration || '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: THEME.muted }} noWrap>
                      {[ac.aircraftType, ac.manufacturer, ac.model].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  size="small"
                  label={ac.availabilityNote || ac.status || 'AVAILABLE'}
                  sx={{
                    height: 20,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    bgcolor: alpha(THEME.info, 0.12),
                    color: THEME.info
                  }}
                />
              </Stack>
            )}
          />
        </Grid>
      </Grid>

      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ color: THEME.ink, fontWeight: 700 }}>
            Action queues
          </Typography>
          <Typography variant="body2" sx={{ color: THEME.muted }}>
            Open a module or use task shortcuts for focused review.
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} xl={4} key={card.key}>
            <WorkloadCard
              card={card}
              loading={loading}
              onOpen={() => navigateCardPrimary(card)}
              onOpenTasks={(e) => {
                setTaskMenuAnchor(e.currentTarget);
                setTaskMenuKey(card.key);
              }}
            />
          </Grid>
        ))}
      </Grid>

      <Menu
        anchorEl={taskMenuAnchor}
        open={Boolean(taskMenuAnchor) && Boolean(taskMenuKey)}
        onClose={() => {
          setTaskMenuAnchor(null);
          setTaskMenuKey(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            borderRadius: 2,
            border: `1px solid ${THEME.border}`,
            boxShadow: `0 12px 32px ${alpha(THEME.ink, 0.12)}`,
            minWidth: 260
          }
        }}
      >
        {(taskActionsByCard[taskMenuKey] || []).map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              item.action();
              setTaskMenuAnchor(null);
              setTaskMenuKey(null);
            }}
            sx={{ fontSize: '0.875rem', py: 1.1 }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 2.5,
          borderRadius: 2.5,
          border: `1px solid ${THEME.border}`,
          bgcolor: THEME.card,
          backgroundImage: `linear-gradient(135deg, ${alpha(THEME.navy, 0.03)} 0%, transparent 55%)`
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink, mb: 0.5 }}>
          Quick coordination shortcuts
        </Typography>
        <Typography variant="body2" sx={{ color: THEME.muted, mb: 2 }}>
          Jump directly into the boards you use most often.
        </Typography>
        <Divider sx={{ mb: 2, borderColor: THEME.border }} />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {shortcuts.map((item) => (
            <Button
              key={item.path + item.label}
              size="small"
              variant="outlined"
              startIcon={item.path === '/ops' ? <Assignment fontSize="small" /> : undefined}
              onClick={() => navigate(item.path)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: THEME.border,
                color: THEME.navy,
                bgcolor: alpha(THEME.navy, 0.02),
                borderRadius: 2,
                px: 1.75,
                '&:hover': {
                  borderColor: THEME.steel,
                  bgcolor: alpha(THEME.steel, 0.08)
                }
              }}
            >
              {item.label}
            </Button>
          ))}
        </Stack>
        <Typography variant="caption" sx={{ color: THEME.muted, display: 'block', mt: 2 }}>
          Ops action queues cover pending Ops handoffs, crew assignment/rejection, BA and ATC clearance,
          and leave approvals. Flight Entry follow-up tracks drafts and AM/CAMO approval waits.
        </Typography>
      </Paper>
    </Box>
  );
};

export default OpsTeamDashboard;
